import { useEffect, useRef, useState } from 'react';
import {
  Close,
  Dropdown,
  Edit,
  SurfaceCard,
  Trash,
} from '@react-apps/design-system';
import type { ExamPreviewQuestion } from '../../domain/entities/content.entity';
import {
  useContentItem,
  useUpdateContentItemBody,
} from '../hooks/use-content-items';
import {
  useBulkDeleteQuestions,
  useRemoveExamQuestionReference,
  useRefetchExamPreview,
  useReorderExamQuestion,
} from '../hooks/use-exams';
import { readQuestionBody } from '../exam-body';
import { sanitizeQuestionPreview } from '../rich-text';
import { isAnnulled } from '../answer-key';
import {
  ANSWER_KEY_ANCHOR,
  questionAnchor,
  useQuestionSpotlight,
} from '../hooks/use-question-spotlight';
import type { EditGuard } from '../hooks/use-edit-guard';
import {
  ContentStatusBadge,
  ErrorState,
  getApiErrorMessage,
  notifyDone,
  notifyFailed,
} from '../display';
import {
  BodyForm,
  type BodyFormBuildRef,
  type BodyFormResult,
} from './BodyForm';
import { QuestionBodyView } from './QuestionBodyView';
import { ConfirmModal } from './ConfirmModal';
import { IconButton } from '@/shared/components/icon-button';
import { QuestionFlagBadges } from './QuestionFlagBadges';
import {
  OfficialCommentField,
  type OfficialCommentRef,
} from './OfficialCommentField';
import {
  QuestionClassificationField,
  type ClassificationRef,
} from './QuestionClassificationField';
import { QuestionClassificationChips } from './QuestionClassificationChips';

/**
 * O id opaco só serve para colar num ticket ou numa query. Storage/clipboard
 * bloqueado (contexto não-seguro, permissão negada) não deve derrubar nada — o
 * pior caso é o clique não fazer efeito.
 */
async function copyId(questionId: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(questionId);
  } catch {
    // Sem clipboard não há o que fazer aqui, e não é erro do usuário.
  }
}

/**
 * Card de uma questão dentro do preview da prova: leitura, edição inline,
 * posição, "anulada" e exclusão.
 *
 * **Em simulado (`isMockExam`) não há excluir nem seleção em massa** — as
 * duas ações agem sobre a questão na prova dona (excluir a tira de todo lugar;
 * transferir troca o dono). O que o simulado oferece é "remover do simulado",
 * que desfaz só o apontador. Editar continua: o simulado aponta para a versão
 * vigente, então corrigir aqui corrige na prova também. Ver primum-monolith
 * `docs/reference/modules/content.md` § Dono vs. apontador.
 *
 * Quem decide se este card está em edição é a **página** (`guard.editingId`),
 * não o card: com o estado por card dava para abrir 80 ao mesmo tempo, e trocar
 * de questão jogava fora o que tinha sido digitado sem avisar.
 */
export function QuestionPreviewCard({
  entry,
  examId,
  selected,
  onToggleSelected,
  guard,
  imageUrlsByFileId,
  isMockExam = false,
}: {
  entry: ExamPreviewQuestion;
  examId: string;
  isMockExam?: boolean;
  selected: boolean;
  onToggleSelected: (questionId: string) => void;
  guard: EditGuard;
  imageUrlsByFileId: Record<string, string>;
}) {
  const { register, spotlight } = useQuestionSpotlight();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isUnlinkOpen, setIsUnlinkOpen] = useState(false);
  const remove = useBulkDeleteQuestions();
  const unlink = useRemoveExamQuestionReference(examId);
  const body = readQuestionBody(entry.question.body);

  const editing = guard.editingId === entry.question.id;
  const isDraft = entry.question.status === 'DRAFT';
  const annulled = isAnnulled(body);

  return (
    <div ref={register(questionAnchor(entry.position))}>
      <SurfaceCard className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            {/* Não existe `Checkbox` no design-system — input nativo é o
                fallback já usado nos outros painéis deste módulo. */}
            {!isMockExam && (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleSelected(entry.question.id)}
                aria-label={`Selecionar a questão ${entry.position}`}
              />
            )}
            {/* Item 5: o título identifica a questão E é o caminho de volta
                para o gabarito. Fora do modo edição, a posição é só isto —
                texto (item 12): um campo numérico focável no header de 80
                cards, cuja consequência é renumerar a prova inteira, era o
                controle mais fácil de disparar sem querer da tela. */}
            <button
              type="button"
              onClick={() => spotlight(ANSWER_KEY_ANCHOR)}
              title="Voltar para o gabarito"
              className="text-(--color-text-primary) hover:text-(--color-accent) text-sm font-semibold"
            >
              Questão {entry.position}
            </button>
            <ContentStatusBadge status={entry.question.status} />
            {/* As três marcações editoriais (anulada, passível de recurso,
                desatualizada) numa peça só — ver QuestionFlagBadges. */}
            <QuestionFlagBadges body={body} />
            <span className="text-(--color-text-secondary) text-xs">
              {body?.format === 'ESSAY' ? 'Dissertativa' : 'Múltipla escolha'}
            </span>
            {/* A classificação fica no header, ao lado do status: numa revisão
                de 80 questões é o que se confere de relance. Clicar abre a
                EDIÇÃO da questão — é lá que ela se altera agora; o chip é
                atalho para o lugar certo, não um segundo editor. */}
            <QuestionClassificationChips
              classifications={entry.classifications}
              onClick={
                editing ? undefined : () => guard.requestEdit(entry.question.id)
              }
            />
          </div>
          <div className="flex items-center gap-1">
            {!editing && (
              <>
                <IconButton
                  label={`Editar a questão ${entry.position}`}
                  icon={<Edit size="sm" />}
                  onClick={() => guard.requestEdit(entry.question.id)}
                />
                {isMockExam ? (
                  <IconButton
                    label={`Remover a questão ${entry.position} do simulado`}
                    icon={<Close size="sm" />}
                    variant="danger"
                    onClick={() => {
                      unlink.reset();
                      setIsUnlinkOpen(true);
                    }}
                  />
                ) : (
                  <IconButton
                    label={`Excluir a questão ${entry.position}`}
                    icon={<Trash size="sm" />}
                    variant="danger"
                    onClick={() => {
                      remove.reset();
                      setIsDeleteOpen(true);
                    }}
                  />
                )}
              </>
            )}
            {/* O que é raro sai do header: o id opaco serve a suporte/debug,
                não a quem está revisando.

                "Marcar como anulada" SAIU daqui: anular virou interruptor nas
                Marcações do form, e manter o item era ter dois controles do
                mesmo campo no mesmo card — um salvando na hora (e invalidando
                o preview por baixo do form aberto) e outro no Salvar. O outro
                caminho de anular segue sendo a grade do gabarito, que se
                desabilita justamente enquanto a questão está em edição.

                Excluir aparece aqui **também**, e a redundância com o ícone é
                de propósito: em modo edição os ícones do header saem, e a
                única forma de apagar a questão era sair da edição primeiro. */}
            <Dropdown
              align="right"
              items={[
                {
                  id: 'copy-id',
                  label: 'Copiar o id da questão',
                  onClick: () => void copyId(entry.question.id),
                },
                isMockExam
                  ? {
                      id: 'remove-reference',
                      label: 'Remover do simulado',
                      onClick: () => {
                        unlink.reset();
                        setIsUnlinkOpen(true);
                      },
                    }
                  : {
                      id: 'delete',
                      label: 'Excluir a questão',
                      onClick: () => {
                        remove.reset();
                        setIsDeleteOpen(true);
                      },
                    },
              ]}
              trigger={
                <span
                  role="img"
                  aria-label={`Mais ações da questão ${entry.position}`}
                  title="Mais ações"
                  className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) flex h-7 w-7 items-center justify-center rounded text-base leading-none"
                >
                  ⋯
                </span>
              }
            />
          </div>
        </div>

        {editing ? (
          <QuestionEditForm
            entry={entry}
            examId={examId}
            guard={guard}
            imageUrlsByFileId={imageUrlsByFileId}
            annulled={annulled}
          />
        ) : body === null ? (
          <p className="text-sm text-red-600">
            Corpo da questão em formato inesperado — abra o item no detalhe de
            conteúdo para inspecionar.
          </p>
        ) : (
          <>
            <QuestionBodyView body={body} />
            {entry.officialComment && (
              <OfficialCommentView comment={entry.officialComment} />
            )}
          </>
        )}

        <ConfirmModal
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() =>
            remove.mutate([entry.question.id], {
              onSuccess: (results) => {
                setIsDeleteOpen(false);
                // O back decide entre apagar de vez e manter o registro; dizer
                // qual dos dois foi é o que a pessoa precisa saber depois.
                notifyDone(
                  results[0]?.mode === 'TOMBSTONED'
                    ? `Questão ${entry.position} saiu da prova (registro preservado)`
                    : `Questão ${entry.position} excluída`,
                );
              },
              // Troca o toast cru da rede global (mesmo id) pela frase
              // traduzida — ex.: `QUESTION_REFERENCED`, rascunho que algum
              // simulado aponta.
              onError: (err) =>
                notifyFailed(err, `Questão ${entry.position} não foi excluída`),
            })
          }
          title={`Excluir a questão ${entry.position}?`}
          description={
            isDraft
              ? 'A questão está em rascunho: será removida de vez, junto com o vínculo com a prova.'
              : 'A questão já foi publicada: ela sai da prova — e de todo simulado que aponta para ela —, mas o registro é preservado (soft delete) porque pode estar referenciado em atividades de aluno.'
          }
          confirmLabel="Excluir"
          loading={remove.isPending}
          error={remove.error}
        />

        <ConfirmModal
          open={isUnlinkOpen}
          onClose={() => setIsUnlinkOpen(false)}
          onConfirm={() =>
            unlink.mutate(entry.question.id, {
              onSuccess: () => {
                setIsUnlinkOpen(false);
                notifyDone(`Questão ${entry.position} saiu do simulado`);
              },
              onError: (err) =>
                notifyFailed(
                  err,
                  `Questão ${entry.position} não saiu do simulado`,
                ),
            })
          }
          title={`Remover a questão ${entry.position} do simulado?`}
          description="Só o simulado deixa de usá-la: a questão continua na prova de origem e em outros simulados. As seguintes sobem uma posição."
          confirmLabel="Remover do simulado"
          loading={unlink.isPending}
          error={unlink.error}
        />
      </SurfaceCard>
    </div>
  );
}

/**
 * O comentário oficial no card em leitura. Some quando a questão não tem — não
 * há "nenhum comentário" a exibir em 80 cards; quem quer criar abre a edição,
 * onde o campo aparece vazio.
 *
 * O texto vem do back já com `<img>` no lugar do token, então é a mesma
 * sanitização de leitura do corpo da questão.
 */
function OfficialCommentView({
  comment,
}: {
  comment: NonNullable<ExamPreviewQuestion['officialComment']>;
}) {
  return (
    <div className="border-(--color-border) space-y-1 border-t pt-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
          Comentário oficial
        </span>
        {comment.author && (
          <span className="text-(--color-text-secondary) text-xs">
            por {comment.author}
          </span>
        )}
      </div>
      <div
        className="rich-text rich-text-read text-(--color-text-primary) text-sm"
        dangerouslySetInnerHTML={{
          __html: sanitizeQuestionPreview(comment.text),
        }}
      />
    </div>
  );
}

/**
 * Modo edição. Busca o item **cru** em vez de reaproveitar o body do preview, e
 * isso não é detalhe: no preview cada imagem já virou `<img src="…">`, e `img`
 * não está no allowlist de escrita do backend — salvar aquele HTML apagaria a
 * tag e a imagem sumiria da questão. O que está no banco (e é o que se edita) é
 * o token `{{img:<fileId>}}`.
 */
function QuestionEditForm({
  entry,
  examId,
  guard,
  imageUrlsByFileId,
  annulled,
}: {
  entry: ExamPreviewQuestion;
  examId: string;
  guard: EditGuard;
  imageUrlsByFileId: Record<string, string>;
  annulled: boolean;
}) {
  const questionId = entry.question.id;
  const { data, isLoading, isError, error } = useContentItem(questionId);
  const update = useUpdateContentItemBody(questionId);
  const reorder = useReorderExamQuestion(examId);
  const refetchPreview = useRefetchExamPreview(examId);
  const { spotlight } = useQuestionSpotlight();

  const [position, setPosition] = useState(String(entry.position));
  const [dirtyBody, setDirtyBody] = useState(false);
  const [dirtyComment, setDirtyComment] = useState(false);
  const [dirtyClassification, setDirtyClassification] = useState(false);
  const buildRef: BodyFormBuildRef = useRef(null);
  const commentRef: OfficialCommentRef = useRef(null);
  const classificationRef: ClassificationRef = useRef(null);

  // A posição muda "de fora" quando outro card é movido — o campo acompanha.
  useEffect(() => setPosition(String(entry.position)), [entry.position]);

  /**
   * Salva o que houver para salvar. Devolve `false` quando NADA foi salvo
   * (validação local reprovada ou erro do servidor) — é isso que faz "Salvar e
   * continuar" da guarda manter a pessoa nesta questão, com o erro à vista, em
   * vez de levá-la para outra deixando o erro atrás.
   */
  const save = async (prebuilt?: BodyFormResult): Promise<boolean> => {
    // O que de fato foi gravado, na ordem em que foi. Vira o texto do aviso:
    // "Questão 5 salva" não diz se o comentário entrou junto, e era isso que
    // obrigava a fechar o card e conferir a olho.
    const saved: string[] = [];
    try {
      // Body inalterado não é reescrito: um PATCH idêntico avança a `version` e
      // grava um `ContentBodyUpdated` que não descreve mudança nenhuma. Mover
      // de posição já tem o seu próprio evento.
      if (dirtyBody) {
        // O clique em Salvar já validou e montou o body; a guarda, que salva
        // sem clique, não — daí o `build()` pelo ref. Um build por salvamento.
        const built = prebuilt ?? buildRef.current?.();
        // `build()` recusou: a mensagem do porquê já está no form (enunciado
        // vazio, alternativa sem texto, gabarito sem letra). O toast diz que
        // NADA foi salvo, que é o que não estava óbvio — o botão só voltava ao
        // normal e a pessoa seguia achando que tinha salvo.
        if (!built) {
          // Caminho da guarda ("Salvar e continuar"), que salva sem passar pelo
          // botão do form — o aviso do `handleSubmit` não roda aqui.
          notifyFailed(
            null,
            `Questão ${entry.position} não foi salva — confira o campo marcado em vermelho`,
          );
          return false;
        }
        await update.mutateAsync({ body: built.body, images: built.images });
        saved.push('enunciado');
      }
      // O comentário é outro `ContentItem`, então é outra requisição — mas o
      // mesmo "Salvar": um segundo botão dentro do card criaria a dúvida de
      // qual salva o quê. Falhar aqui cai no `catch` comum lá embaixo e mantém
      // a pessoa na questão, com o erro à vista, como qualquer outra falha.
      if (classificationRef.current?.isDirty()) {
        await classificationRef.current.save();
        saved.push('classificação');
      }
      if (commentRef.current?.isDirty()) {
        await commentRef.current.save();
        saved.push('comentário');
      }
      const movedTo = await commitPosition();

      // UMA releitura, esperada, depois de TODAS as escritas. As invalidações
      // que cada escrita acima dispara saem com a foto de antes do reorder e
      // chegam depois dele — sem esta linha, a tela volta para a ordem antiga
      // e o "mover para a posição N" parece não ter funcionado. Ver
      // `useRefetchExamPreview`.
      await refetchPreview();

      if (movedTo !== null) {
        saved.push(`posição ${entry.position} → ${movedTo}`);
        // Depois da releitura: o card só existe na posição nova quando a lista
        // já foi re-renderizada com ela.
        spotlight(questionAnchor(movedTo));
      }

      notifyDone(
        saved.length === 0
          ? `Questão ${entry.position}: nada mudou, nada foi salvo`
          : `Questão ${movedTo ?? entry.position} salva — ${saved.join(', ')}`,
      );
      return true;
    } catch (err) {
      // O inline (`update.error`/`reorder.error`, erro do campo) diz ONDE
      // falhou e fica ao lado do campo; o toast diz QUE falhou e aparece mesmo
      // com o card fora da área visível — que é o caso numa prova de 80.
      notifyFailed(err, `Questão ${entry.position} não foi salva`);
      return false;
    }
  };

  /** `null` = o campo não descreve uma posição válida. */
  const parsedPosition = (): number | null => {
    const value = Number(position);
    return Number.isInteger(value) && value >= 1 ? value : null;
  };

  /** Posição efetiva depois de mover, ou `null` quando não havia o que mover. */
  const commitPosition = async (): Promise<number | null> => {
    const target = parsedPosition();
    if (target === null || target === entry.position) return null;
    const links = await reorder.mutateAsync({
      questionId,
      newPosition: target,
    });
    // Item 13: a lista reordena e o card sai de onde a pessoa estava olhando.
    // A piscada na posição efetiva (o back satura no fim da prova) é o que diz
    // para onde ele foi — mesmo efeito do clique no gabarito, mesmo hook. Quem
    // pisca é o `save()`, depois da releitura: aqui o DOM ainda não tem o card
    // na posição nova.
    const moved = links.find((link) => link.targetContentId === questionId);
    return moved?.position ?? null;
  };

  const positionChanged =
    parsedPosition() !== null && parsedPosition() !== entry.position;

  // Sujo = body alterado OU posição alterada. Os dois moram neste card, então é
  // ele que soma antes de registrar na guarda.
  const isDirty =
    dirtyBody || positionChanged || dirtyComment || dirtyClassification;
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });

  // `guard.register` é estável; `guard` inteiro não é (o hook devolve um objeto
  // novo por render), e depender dele reexecutaria isto a cada render da página.
  const { register: registerWithGuard } = guard;
  useEffect(
    () =>
      registerWithGuard(questionId, {
        isDirty: () => isDirtyRef.current,
        save: () => saveRef.current(),
      }),
    [registerWithGuard, questionId],
  );

  if (isLoading) {
    return (
      <p className="text-(--color-text-secondary) text-sm">
        Carregando a questão…
      </p>
    );
  }
  if (isError || !data) {
    return (
      <ErrorState
        message={getApiErrorMessage(error, 'Não foi possível abrir a questão.')}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Uma linha, sem moldura: é um campo só, e o card aberto já é a peça mais
          alta da tela. */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-(--color-text-secondary) flex items-center gap-2 text-xs">
          Mover para a posição
          {/* Input nativo: o `Input` do design-system embute label/wrapper. */}
          <input
            type="number"
            min={1}
            value={position}
            disabled={reorder.isPending}
            onChange={(e) => setPosition(e.target.value)}
            title="A prova é renumerada 1..N a partir daqui — a questão é movida, as do meio deslocam"
            className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) h-7 w-14 rounded border text-center text-sm font-semibold tabular-nums disabled:opacity-50"
          />
        </label>
        {reorder.isPending && (
          <span className="text-(--color-text-secondary) text-xs">
            movendo…
          </span>
        )}
        {reorder.error && (
          <span className="text-xs text-red-600" role="alert">
            {getApiErrorMessage(reorder.error, 'Falha ao mover')}
          </span>
        )}
      </div>

      <BodyForm
        type="QUESTION"
        body={data.item.body}
        pending={update.isPending || reorder.isPending}
        error={update.error}
        imageUrlsByFileId={imageUrlsByFileId}
        annulled={annulled}
        onDirtyChange={setDirtyBody}
        buildRef={buildRef}
        // O botão Salvar e o "Salvar e continuar" da guarda passam pelo MESMO
        // `save()`: dois caminhos de salvamento divergiriam na primeira mudança.
        onSubmit={(result) => {
          void save(result).then((ok) => {
            if (ok) guard.close();
          });
        }}
        onCancel={guard.close}
        /* Classificação e comentário entram DENTRO do form, antes do Salvar —
           é o que deixa um botão só, no fim do card, salvando tudo o que está
           na tela. A ordem segue a da revisão: o que a questão é, depois o que
           se lê após respondê-la. */
        extraSections={
          <>
            <div className="border-(--color-border) border-t pt-3">
              <QuestionClassificationField
                questionId={questionId}
                current={entry.classifications}
                onDirtyChange={setDirtyClassification}
                fieldRef={classificationRef}
              />
            </div>
            <div className="border-(--color-border) border-t pt-3">
              <OfficialCommentField
                questionId={questionId}
                comment={entry.officialComment}
                imageUrlsByFileId={imageUrlsByFileId}
                onDirtyChange={setDirtyComment}
                fieldRef={commentRef}
              />
            </div>
          </>
        }
      />
    </div>
  );
}
